import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ageInDays = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};

export default function LiveItems() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/items?stage=live_on_marketplace&limit=200`, { withCredentials: true });
      const sorted = [...res.data].sort((a, b) => new Date(b.listed_at || b.created_at) - new Date(a.listed_at || a.created_at));
      setItems(sorted);
    } catch (error) {
      toast.error('Failed to load live items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      <div>
        <header className="bg-white border-b border-[#E9E9E9] px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="live-items-back-button">
              <ArrowLeft className="w-5 h-5 text-[#474747]" />
            </button>
            <div>
              <h1 className="text-2xl font-heading font-semibold text-[#474747]">Live Items</h1>
              <p className="text-sm text-gray-500">{items.length} item{items.length === 1 ? '' : 's'} currently live on the marketplace</p>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
          {loading ? (
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-12 text-center text-gray-500">Loading live items...</div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E9E9E9] p-12 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No items are live on the marketplace right now.</p>
            </div>
          ) : (
            <>
            {/* Mobile card list */}
            <div className="md:hidden space-y-3" data-testid="live-items-mobile-list">
              {items.map(item => {
                const listed = item.listed_at || item.created_at;
                const age = ageInDays(listed);
                const photo = item.photo_urls?.[0] || item.listers_photo_urls?.[0] || item.seller_photo_urls?.[0] || null;
                return (
                  <div key={item.id} onClick={() => navigate(`/item/${item.id}`)}
                    className="bg-white rounded-xl border border-[#E9E9E9] p-3.5 flex gap-3 active:bg-gray-50">
                    {photo ? (
                      <img src={photo} alt={item.title} className="w-16 h-16 object-cover rounded-lg border border-[#E9E9E9] shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-100 border border-[#E9E9E9] flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-[#474747] leading-snug">{item.title}</p>
                        <p className="text-sm font-bold text-[#474747] shrink-0">${Number(item.listed_price ?? item.nominated_sale_price ?? 0).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${age > 30 ? 'bg-red-100 text-red-700' : age > 14 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                          {age === null ? '—' : age === 0 ? 'Today' : `${age} day${age === 1 ? '' : 's'}`}
                        </span>
                        {item.marketplace_id && <span className="text-[11px] font-mono text-gray-500">ID {item.marketplace_id}</span>}
                        <span className="text-[11px] text-gray-400 ml-auto">{listed ? new Date(listed).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E9E9E9] bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Photo</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Item</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Marketplace ID</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Date Listed</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Age</th>
                    <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Listed Price</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const listed = item.listed_at || item.created_at;
                    const age = ageInDays(listed);
                    const photo = item.photo_urls?.[0] || item.listers_photo_urls?.[0] || item.seller_photo_urls?.[0] || null;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => navigate(`/item/${item.id}`)}
                        className="border-b border-[#F2F2F0] last:border-0 hover:bg-[#F7F9F2] cursor-pointer transition-colors"
                        data-testid={`live-item-row-${item.id}`}
                      >
                        <td className="px-4 py-3">
                          {photo ? (
                            <img src={photo} alt={item.title} className="w-14 h-14 object-cover rounded-lg border border-[#E9E9E9]" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-gray-100 border border-[#E9E9E9] flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-300" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-[#474747]">{item.title}</p>
                          <p className="text-xs text-gray-400">{item.reference_code}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono text-gray-600" data-testid={`marketplace-id-${item.id}`}>
                            {item.marketplace_id || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {listed ? new Date(listed).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${age > 30 ? 'bg-red-100 text-red-700' : age > 14 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {age === null ? '—' : age === 0 ? 'Today' : `${age} day${age === 1 ? '' : 's'}`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#474747]">
                          ${Number(item.listed_price ?? item.nominated_sale_price ?? 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
